import { useState, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  OutlinedInput,
  InputAdornment,
  Switch,
  Card,
  Dialog,
  Drawer,
} from "@mui/material";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import {
  ArrowBack as ArrowBackIcon,
  InfoOutlined as InfoOutlinedIcon,
  Login as LoginIcon,
  ExitToApp as ExitToAppIcon,
  LocalParking as LocalParkingIcon,
  Add as AddIcon,
  Close as CloseIcon,
  ChevronRight as ChevronRightIcon,
  WarningAmberRounded as WarningIcon,
} from "@mui/icons-material";
import { Building, EntryPointType } from "./ContributeNotes";
import { Option } from "@/types/common";
import { getIconForSectionType } from "@/utils/buildingIcons";
import NamingGuidelinesModal from "./NamingGuidelinesModal";
import EntryPointDetailsForm from "./EntryPointDetailsForm";
import Icons from "@/assets/index";

interface BuildingInfoProps {
  building: Building;
  onUpdateBuilding: (id: string, updates: Partial<Building>) => void;
  onBack: () => void;
  initialShowErrors?: boolean;
  isEditingDraft?: boolean;
}

const styles = {
  container: {
    backgroundColor: "#F8F8F8",
    minHeight: "100vh",
    paddingBottom: "80px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    position: "sticky" as const,
    top: 0,
    backgroundColor: "#fff",
    zIndex: 10,
    padding: "3% 4%",
    borderBottom: "1px solid #e0e0e0",
  },
  backButton: {
    marginRight: "8px",
    color: "#000",
  },
  title: {
    fontSize: "4.71vw",
    fontWeight: 600,
    color: "#000",
    flex: 1,
  },
  mainContent: {
    padding: "4%",
  },
  sectionTitle: {
    fontSize: "4.24vw",
    fontWeight: 600,
    color: "#191919",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "12px",
  },
  infoIcon: {
    color: "#666",
    fontSize: "4.71vw",
  },
  nameInput: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    marginBottom: "24px",
    "& fieldset": { borderColor: "#E0E0E0" },
    "&:hover fieldset": { borderColor: "#C0C0C0" },
    "&.Mui-focused fieldset": { borderColor: "#9443DF" },
  },
  editBtn: {
    color: "#9443DF",
    fontWeight: 600,
    textTransform: "none" as const,
    fontSize: "3.5vw",
  },
  exitPointCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px",
    backgroundColor: "#fff",
    borderRadius: "12px",
    border: "1px solid #E0E0E0",
    marginBottom: "24px",
    boxShadow: "none",
  },
  exitPointTitle: {
    fontSize: "4.24vw",
    fontWeight: 600,
    color: "#191919",
    marginBottom: "2px",
  },
  exitPointSubtitle: {
    fontSize: "3.29vw",
    color: "#828282",
  },
  sectionHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  addButton: {
    background: "#F0DEFF !important",
    color: "#8D28E6",
    textTransform: "none" as const,
    fontWeight: 800,
    fontSize: "3vw",
    padding: "4px 12px",
    borderRadius: "16px",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#e2c5ff",
      boxShadow: "none",
    },
  },
  emptyStateCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 16px",
    backgroundColor: "#fff",
    borderRadius: "12px",
    border: "1px solid #E0E0E0",
    boxShadow: "none",
  },
  emptyStateIconWrapper: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    backgroundColor: "#F5F5F5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "16px",
  },
  emptyStateIcon: {
    color: "#A0A0A0",
    fontSize: "6.59vw",
  },
  emptyStateTitle: {
    fontSize: "3.8vw",
    fontWeight: 600,
    color: "#191919",
    marginBottom: "8px",
  },
  emptyStateSubtitle: {
    fontSize: "3.29vw",
    color: "#626262",
    textAlign: "center",
  },
  entryPointCardAdded: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    backgroundColor: "#fff",
    borderRadius: "12px",
    border: "1px solid #E0E0E0",
    marginBottom: "12px",
    cursor: "pointer",
  },
  typeIconWrapperSmall: {
    width: "40px",
    height: "40px",
    borderRadius: "8px",
    backgroundColor: "#F0DEFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "16px",
  },
  entryPointName: {
    flex: 1,
    fontSize: "4vw",
    fontWeight: 700,
    color: "#0F172A",
  },
  drawerContent: {
    padding: "24px",
    borderTopLeftRadius: "24px",
    borderTopRightRadius: "24px",
  },
  drawerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  drawerTitle: {
    fontSize: "4.71vw",
    fontWeight: 700,
    color: "#0F172A",
  },
  drawerSubtitle: {
    fontSize: "3.29vw",
    color: "#64748B",
    marginBottom: "24px",
  },
  drawerGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
  },
  typeCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px 12px",
    backgroundColor: "#F8FAFC",
    borderRadius: "12px",
    border: "2px solid #F1F5F9",
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.02)",
    cursor: "pointer",
  },
  typeIconWrapper: {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    backgroundColor: "#F0DEFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "12px",
  },
  typeIcon: {
    color: "#8D28E6",
    fontSize: "5.65vw",
  },
  typeIconCustomWrapper: {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    backgroundColor: "#9443DF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "12px",
  },
  typeIconCustom: {
    color: "#fff",
    fontSize: "5.65vw",
  },
  typeName: {
    fontSize: "3.29vw",
    color: "#334155",
    fontWeight: 500,
    textAlign: "center",
  },
  typeNameCustom: {
    fontSize: "3.29vw",
    color: "#9443DF",
    fontWeight: 500,
    textAlign: "center",
  },
  bottomBar: {
    position: "fixed" as const,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#fff",
    padding: "16px",
    borderTop: "1px solid #e0e0e0",
    display: "flex",
    gap: "12px",
    zIndex: 1000,
  },
  draftButton: {
    flex: 1,
    textTransform: "none" as const,
    color: "#191919",
    fontWeight: 700,
    fontSize: "3.76vw",
    padding: "12px",
    borderRadius: "30px",
    backgroundColor: "#F0DEFF",
    "&:hover": { backgroundColor: "#e2c5ff" },
  },
  submitButton: {
    flex: 1,
    textTransform: "none" as const,
    color: "#fff",
    fontWeight: 700,
    fontSize: "3.76vw",
    padding: "12px",
    borderRadius: "30px",
    background: "linear-gradient(180deg, #9D79F4 0%, #7747FB 100%)",
    "&:hover": { backgroundColor: "#6034d6" },
  },
};

export default function BuildingInfo({ 
  building, 
  onUpdateBuilding, 
  onBack,
  initialShowErrors = false,
  isEditingDraft = false,
}: BuildingInfoProps) {
  const { action, contribution_id } = useParams<{ action?: string, contribution_id?: string }>();
  const isUpdateMode = action === 'update' && !!contribution_id;
  const showUpdateButtons = isUpdateMode && !isEditingDraft;
  
  const [drawerType, setDrawerType] = useState<'entry' | 'exit' | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEntryPoint, setSelectedEntryPoint] = useState<EntryPointType | null>(null);
  const [isExitPointSelected, setIsExitPointSelected] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(initialShowErrors);

  const isEntryPointValid = (ep: EntryPointType) => {
    const hasPhotos = ep.photos && ep.photos.filter((p) => p !== null).length === 3;
    const hasNotes = (ep.notesSelection && Object.values(ep.notesSelection).some((arr: Option[]) => arr && arr.length > 0)) || (ep.tags && ep.tags.length > 0);
    return hasPhotos && hasNotes;
  };

  const handleSubmitBuilding = () => {
    const points = [...building.entryPoints, ...building.exitPoints];
    const allValid = points.length > 0 && points.every(isEntryPointValid);
    
    if (building.entryPoints.length === 0) {
      setShowValidationErrors(true);
      toast.error("Please add at least one entry point");
      return;
    }

    if (building.hasSeparateExit && building.exitPoints.length === 0) {
      setShowValidationErrors(true);
      toast.error("Please add at least one exit point");
      return;
    }

    if (!allValid) {
      setShowValidationErrors(true);
      toast.error("Please fill all the required fields");
      return;
    }
    
    onBack();
  };

  const [pointToDelete, setPointToDelete] = useState<(EntryPointType & { _pointType: 'entry' | 'exit' }) | null>(null);
  const [isLongPressTriggered, setIsLongPressTriggered] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTouchMode = useRef(false);

  const handleTouchStart = (ep: EntryPointType, type: 'entry' | 'exit', e: React.TouchEvent | React.MouseEvent) => {
    if (e.type === 'touchstart') isTouchMode.current = true;
    if (e.type === 'mousedown' && isTouchMode.current) return;

    setIsLongPressTriggered(false);
    longPressTimer.current = setTimeout(() => {
      setIsLongPressTriggered(true);
      setPointToDelete({ ...ep, _pointType: type });
    }, 500);
  };

  const handleTouchEnd = (e: React.TouchEvent | React.MouseEvent) => {
    if (e.type === 'mouseup' && isTouchMode.current) return;
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  };

  const handleClickPoint = (ep: EntryPointType, type: 'entry' | 'exit') => {
    if (isTouchMode.current) {
      setTimeout(() => { isTouchMode.current = false; }, 100);
    }
    if (!isLongPressTriggered) {
      setIsExitPointSelected(type === 'exit');
      setSelectedEntryPoint(ep);
    }
  };

  const confirmDeletePoint = () => {
    if (pointToDelete) {
      if (pointToDelete._pointType === 'exit') {
        const newExitPoints = building.exitPoints.filter(ep => ep.id !== pointToDelete.id);
        onUpdateBuilding(building.id, { exitPoints: newExitPoints });
      } else {
        const newEntryPoints = building.entryPoints.filter(ep => ep.id !== pointToDelete.id);
        onUpdateBuilding(building.id, { entryPoints: newEntryPoints });
      }
      if (isUpdateMode) toast.success("Entry/Exit point deleted successfully");
    }
    setPointToDelete(null);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdateBuilding(building.id, { name: e.target.value });
  };

  const handleEditClick = () => {
    if (!isEditingName) {
      const hideGuidelines = localStorage.getItem("hideNamingGuidelines") === "true";
      if (!hideGuidelines) {
        setModalOpen(true);
      }
    }
    setIsEditingName(!isEditingName);
  };

  const handleInfoClick = () => {
    setModalOpen(true);
  };

  const handleEntryTypeSelect = (type: string) => {
    const newPoint = { id: Date.now().toString(), type };
    if (drawerType === 'exit') {
      onUpdateBuilding(building.id, { exitPoints: [...building.exitPoints, newPoint] });
    } else {
      onUpdateBuilding(building.id, { entryPoints: [...building.entryPoints, newPoint] });
    }
    setDrawerType(null);
  };

  const handleSaveEntryPoint = (updatedEntryPoint: EntryPointType) => {
    if (isExitPointSelected) {
      const newExitPoints = building.exitPoints.map(ep => ep.id === updatedEntryPoint.id ? updatedEntryPoint : ep);
      onUpdateBuilding(building.id, { exitPoints: newExitPoints });
    } else {
      const newEntryPoints = building.entryPoints.map(ep => ep.id === updatedEntryPoint.id ? updatedEntryPoint : ep);
      onUpdateBuilding(building.id, { entryPoints: newEntryPoints });
    }
  };

  const getIconForType = (type: string) => getIconForSectionType(type, "5.65vw");

  const renderPointSection = (type: 'entry' | 'exit') => {
    const isEntry = type === 'entry';
    const title = isEntry ? (building.hasSeparateExit ? "Entry Point" : "Entry/Exit Point") : "Exit Point";
    const points = isEntry ? building.entryPoints : building.exitPoints;
    const emptyStateText = isEntry ? "No entry points added yet" : "No exit points added yet";
    const emptyStateActionText = isEntry ? "entrance" : "exit";
    const IconComponent = isEntry ? LoginIcon : ExitToAppIcon;

    return (
      <Box sx={{ marginBottom: "24px" }}>
        <Box sx={styles.sectionHeaderRow}>
          <Typography sx={styles.sectionTitle} style={{ marginBottom: 0 }}>
            {title}
          </Typography>
          <Button
            variant="contained"
            sx={styles.addButton}
            onClick={() => setDrawerType(type)}
          >
            Add
          </Button>
        </Box>

        {points.length === 0 ? (
          <Card sx={styles.emptyStateCard}>
            <Box sx={styles.emptyStateIconWrapper}>
              <IconComponent sx={styles.emptyStateIcon} />
            </Box>
            <Typography sx={styles.emptyStateTitle}>{emptyStateText}</Typography>
            <Typography sx={styles.emptyStateSubtitle}>
              Click <span style={{ color: "#8D28E6", fontWeight: 600 }}>Add</span> to insert an {emptyStateActionText} section
            </Typography>
          </Card>
        ) : (
          <Box>
            {points.map((ep) => (
              <Box
                key={ep.id}
                sx={{ ...styles.entryPointCardAdded, userSelect: "none" }}
                onClick={() => handleClickPoint(ep, type)}
                onMouseDown={(e) => handleTouchStart(ep, type, e)}
                onMouseUp={handleTouchEnd}
                onMouseLeave={handleTouchEnd}
                onTouchStart={(e) => handleTouchStart(ep, type, e)}
                onTouchEnd={handleTouchEnd}
                onTouchMove={handleTouchEnd}
                onContextMenu={(e) => e.preventDefault()}
              >
                <Box sx={styles.typeIconWrapperSmall}>
                  {getIconForType(ep.type)}
                </Box>
                <Typography sx={{ ...styles.entryPointName, flexGrow: 1 }}>{ep.name || ep.type}</Typography>
                {showValidationErrors && !isEntryPointValid(ep) && (
                  <Box component="img" src={Icons.Alert} sx={{ width: 20, height: 20, mr: 1 }} />
                )}
                <ChevronRightIcon sx={{ color: "#666" }} />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={styles.container}>
      {/* Header */}
      <Box sx={styles.header}>
        <IconButton sx={styles.backButton} onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={styles.title}>Building Info</Typography>
      </Box>

      <Box sx={styles.mainContent}>
        {/* Name Section */}
        <Typography sx={styles.sectionTitle}>
          Name 
          <IconButton onClick={handleInfoClick} size="small" sx={{ padding: 0 }}>
            <InfoOutlinedIcon sx={styles.infoIcon} />
          </IconButton>
        </Typography>
        <OutlinedInput
          value={building.name}
          onChange={handleNameChange}
          readOnly={!isEditingName}
          fullWidth
          sx={styles.nameInput}
          endAdornment={
            <InputAdornment position="end">
              <Button
                sx={styles.editBtn}
                onClick={handleEditClick}
              >
                {isEditingName ? "Done" : "Edit"}
              </Button>
            </InputAdornment>
          }
        />

        {/* Exit Point Toggle */}
        <Card sx={styles.exitPointCard}>
          <Box>
            <Typography sx={styles.exitPointTitle}>Exit Point</Typography>
            <Typography sx={styles.exitPointSubtitle}>
              For buildings with separate exit routes
            </Typography>
          </Box>
          <Switch
            checked={building.hasSeparateExit}
            onChange={(e) => onUpdateBuilding(building.id, { hasSeparateExit: e.target.checked })}
            color="secondary"
          />
        </Card>

        {renderPointSection('entry')}
        {building.hasSeparateExit && renderPointSection('exit')}
      </Box>

      {/* Bottom Sticky Bar */}
      <Box sx={styles.bottomBar}>
        <Button sx={styles.draftButton} onClick={onBack}>{showUpdateButtons ? "Cancel" : "Draft"}</Button>
        <Button sx={styles.submitButton} onClick={handleSubmitBuilding}>{showUpdateButtons ? "Update" : "Submit"}</Button>
      </Box>

      {/* Drawer for Entry Type Selection */}
      <Drawer
        anchor="bottom"
        open={Boolean(drawerType)}
        onClose={() => setDrawerType(null)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: "24px",
            borderTopRightRadius: "24px",
          },
        }}
      >
        <Box sx={styles.drawerContent}>
          <Box
            sx={{
              width: "40px",
              height: "4px",
              backgroundColor: "#CBD5E1",
              borderRadius: "2px",
              margin: "0 auto 16px auto",
            }}
          />
          <Box sx={styles.drawerHeader}>
            <Typography sx={styles.drawerTitle}>Select {drawerType === 'exit' ? 'Exit' : 'Entry'} Type</Typography>
            <IconButton onClick={() => setDrawerType(null)} size="small" sx={{ backgroundColor: "#0000008A", color: "#fff", "&:hover": { backgroundColor: "#000" } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography sx={styles.drawerSubtitle}>
            Choose the type of {drawerType === 'exit' ? 'exit' : 'entrance'} you want to add to the system.
          </Typography>

          <Box sx={styles.drawerGrid}>
            <Box sx={styles.typeCard} onClick={() => handleEntryTypeSelect(`Main ${drawerType === 'exit' ? 'Exit' : 'Entrance'}`)}>
              <Box sx={styles.typeIconWrapper}>
                <LoginIcon sx={styles.typeIcon} />
              </Box>
              <Typography sx={styles.typeName}>Main {drawerType === 'exit' ? 'Exit' : 'Entrance'}</Typography>
            </Box>
            <Box sx={styles.typeCard} onClick={() => handleEntryTypeSelect(`Back Side ${drawerType === 'exit' ? 'Exit' : 'Entrance'}`)}>
              <Box sx={styles.typeIconWrapper}>
                <ExitToAppIcon sx={{ ...styles.typeIcon, transform: "scaleX(-1)" }} />
              </Box>
              <Typography sx={styles.typeName}>Back Side {drawerType === 'exit' ? 'Exit' : 'Entrance'}</Typography>
            </Box>
            <Box sx={styles.typeCard} onClick={() => handleEntryTypeSelect(`Parking ${drawerType === 'exit' ? 'Exit' : 'Entrance'}`)}>
              <Box sx={styles.typeIconWrapper}>
                <LocalParkingIcon sx={styles.typeIcon} />
              </Box>
              <Typography sx={styles.typeName}>Parking {drawerType === 'exit' ? 'Exit' : 'Entrance'}</Typography>
            </Box>
            <Box sx={styles.typeCard} onClick={() => handleEntryTypeSelect("Custom")}>
              <Box sx={styles.typeIconCustomWrapper}>
                <AddIcon sx={styles.typeIconCustom} />
              </Box>
              <Typography sx={styles.typeNameCustom}>Custom</Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>

      <NamingGuidelinesModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />

      <EntryPointDetailsForm
        open={!!selectedEntryPoint}
        onClose={() => setSelectedEntryPoint(null)}
        buildingName={building.name}
        entryPoint={selectedEntryPoint}
        isExit={isExitPointSelected}
        onSave={handleSaveEntryPoint}
        hasError={showValidationErrors && selectedEntryPoint ? !isEntryPointValid(selectedEntryPoint) : false}
        availableEntrances={building.entryPoints}
        isEditingDraft={isEditingDraft}
      />

      <Dialog 
        open={!!pointToDelete} 
        onClose={() => setPointToDelete(null)}
        PaperProps={{ sx: { borderRadius: "24px", padding: "16px", maxWidth: "400px", width: "90%", textAlign: "center" } }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <Box sx={{ backgroundColor: "#FFE5E5", borderRadius: "50%", width: 64, height: 64, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <WarningIcon sx={{ color: "#FF4D4D", fontSize: 32 }} />
          </Box>
        </Box>
        <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#000", mb: 3 }}>
          Do you want to delete {pointToDelete?.name || pointToDelete?.type}?
        </Typography>
        <Box sx={{ backgroundColor: "#F8F8F8", borderRadius: "12px", padding: "16px", textAlign: "left", mb: 3 }}>
          <Typography sx={{ fontSize: "14px", color: "#333", mb: 2 }}>
            Removing this {pointToDelete?._pointType === 'exit' ? 'exit' : 'entry'} point will permanently delete its photos, video, and notes.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: "16px" }}>
          <Button fullWidth sx={{ backgroundColor: "#F9E4E4", color: "#FF4D4D", borderRadius: "8px", padding: "12px", fontWeight: 700, textTransform: "none" }} onClick={() => setPointToDelete(null)}>No</Button>
          <Button fullWidth sx={{ backgroundColor: "#FF4D4D", color: "#FFF", borderRadius: "8px", padding: "12px", fontWeight: 700, textTransform: "none", "&:hover": { backgroundColor: "#E60000" } }} onClick={confirmDeletePoint}>Yes</Button>
        </Box>
      </Dialog>
    </Box>
  );
}
